import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, errText } from "@/lib/api";
import { useLang, REGIONS } from "@/lib/i18n";
import { LangSwitch } from "@/components/Layout";
import RegionSwitcher from "@/components/RegionSwitcher";
import { LogoMark } from "@/components/Logo";
import Flag from "@/components/Flag";
import { CheckCircle2, Globe2, Loader2, BadgeCheck, ArrowRight, HeartPulse, PiggyBank, BarChart3, Wrench } from "lucide-react";
import { Link } from "react-router-dom";

const HOMES = { admin: "/admin", superadmin: "/superadmin", assessor: "/assessor", employer: "/employer" };
const isPreview = /localhost|127\.0\.0\.1|preview\.emergentagent\.com/.test(window.location.hostname);

export default function Login() {
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, register, user } = useAuth();
  const { t, regionInfo, region, setRegion } = useLang();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) navigate(HOMES[user.role] || "/app", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      const u = mode === "login" ? await login(form.email, form.password) : await register(form.name, form.email, form.password);
      navigate(HOMES[u.role] || "/app");
    } catch (err) {
      setError(errText(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const input = "w-full h-12 px-4 rounded-xl border border-slate-200 bg-slate-50/60 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-600 focus:border-transparent transition-colors";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between overflow-hidden bg-teal-950 text-white p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-950 via-teal-900 to-slate-900" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-700/20 blur-3xl" />
        <div className="absolute -bottom-32 -left-20 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl" />
        <div data-testid="login-flag-wall" className="absolute right-8 top-1/2 -translate-y-1/2 hidden 2xl:block z-10">
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400 mb-3 text-center">Live in 16 countries</p>
          <div className="grid grid-cols-2 gap-3">
            {REGIONS.map((r, i) => (
              <button key={r.code} type="button" onClick={() => setRegion(r.code)} title={r.name}
                data-testid={`login-flagwall-${r.code}`} aria-label={`Set region to ${r.name}`}
                className={`group flex flex-col items-center gap-1.5 rounded-xl px-3 py-2 border transition-all hover:scale-110 hover:-translate-y-1 ${r.code === region ? "bg-amber-400/20 border-amber-400/70 ring-1 ring-amber-400/60" : "bg-white/[0.07] border-white/10 hover:bg-white/15"}`}>
                <Flag code={r.code} className="w-12 h-8 rounded-md shadow-md shadow-black/30 border border-white/20 flag-float" style={{ animationDelay: `${i * 0.22}s` }} />
                <span className="text-[10px] font-semibold text-teal-100 group-hover:text-white">{r.name}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="relative flex items-center gap-3">
          <LogoMark size={46} />
          <div className="leading-tight">
            <p className="font-heading font-extrabold text-lg">FSCD Global Skills</p>
            <p className="text-[10px] uppercase tracking-[0.14em] text-teal-300">Training · Certification · Jobs</p>
          </div>
        </div>
        <div className="relative space-y-2 max-w-md">
          <h1 className="font-heading text-3xl xl:text-4xl font-extrabold leading-tight">Train. Certify. <span className="text-amber-400">Get Hired.</span></h1>
          <p className="text-sm text-teal-100/90 leading-relaxed">A skills and workforce platform: structured courses with live classes, verified certificates, and direct pathways to real roles.</p>
          <div className="rounded-3xl overflow-hidden border border-white/15 shadow-2xl shadow-black/30" data-testid="login-hero-illustration">
            <img src="/illustrations/login-hero.png" alt="Learn, earn a certificate, get hired — illustration" className="w-full h-32 2xl:h-36 object-cover" />
          </div>
          <div data-testid="login-course-areas">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-400 mb-2.5">What you'll train in</p>
            <div className="grid grid-cols-2 gap-2.5">
              {[
                [<HeartPulse size={16} key="h" />, "Healthcare & Patient Care", "Clinical assistance, patient coordination, community health work"],
                [<PiggyBank size={16} key="p" />, "Financial Literacy", "Budgeting, savings, digital money skills for everyday life"],
                [<BarChart3 size={16} key="b" />, "Office Admin & Data", "Front/back-office operations, records, reporting & analytics"],
                [<Wrench size={16} key="w" />, "Skilled Trades & Job Readiness", "Hands-on vocational skills, workplace and interview readiness"],
              ].map(([icon, title, desc]) => (
                <div key={title} className="rounded-xl bg-white/[0.07] border border-white/10 p-2.5">
                  <p className="flex items-center gap-1.5 text-[12px] font-bold text-white"><span className="text-amber-300">{icon}</span>{title}</p>
                  <p className="text-[10.5px] text-teal-200/80 leading-snug mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            {[
              [<BadgeCheck size={15} key="b" />, "Every course ends in a verified certificate (QR + PDF) and a job pathway"],
              [<Globe2 size={15} key="d" />, "13 languages · 16 countries · live video classes · installable on any phone"],
            ].map(([icon, txt]) => (
              <p key={txt} className="flex items-center gap-2.5 text-xs text-teal-100">
                <span className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center text-amber-300 shrink-0">{icon}</span>{txt}
              </p>
            ))}
            <div data-testid="login-flags-strip" className="flex flex-wrap items-center gap-1 2xl:hidden">
              {REGIONS.map((r, i) => (
                <button key={r.code} type="button" title={r.name} data-testid={`login-flag-${r.code}`}
                  onClick={() => setRegion(r.code)} aria-label={`Set region to ${r.name}`}
                  className={`rounded-md border p-0.5 transition-all hover:scale-110 ${r.code === region ? "border-amber-400 ring-1 ring-amber-400/70" : "border-white/20 hover:border-white/50"}`}>
                  <Flag code={r.code} className="w-7 h-[19px] rounded-[3px] flag-float" style={{ animationDelay: `${i * 0.18}s` }} />
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3 text-xs font-semibold text-teal-200" data-testid="login-journey-strip">
            {["Learn", "Practice", "Certify", "Get Hired"].map((s, i, arr) => (
              <span key={s} className="flex items-center gap-3">
                <span className="px-3 py-1.5 rounded-full bg-white/10 border border-white/15">{s}</span>
                {i < arr.length - 1 && <ArrowRight size={13} className="text-amber-400" />}
              </span>
            ))}
          </div>
        </div>
        <div className="relative flex items-center justify-between text-xs text-teal-300/80">
          <p>© 2026 FSCD Global Skills</p>
          <p data-testid="login-region-label" className="flex items-center gap-1.5"><Flag code={regionInfo.code} className="w-5 h-3.5 rounded-[2px]" /> {regionInfo.name} · English first</p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col min-h-screen">
        <div className="flex items-center justify-between p-4 sm:p-6">
          <div data-testid="login-brand" className="flex items-center gap-2 lg:hidden">
            <LogoMark size={36} />
            <span className="font-heading font-extrabold text-teal-950">{t("appName")}</span>
          </div>
          <span className="hidden lg:block" />
          <div className="flex items-center gap-2">
            <RegionSwitcher />
            <LangSwitch />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 pb-10">
          <div className="w-full max-w-md fade-in">
            <div className="lg:hidden rounded-2xl mb-5 overflow-hidden bg-[#F0FDF9] border border-teal-100" data-testid="login-mobile-illustration">
              <img src="/illustrations/login-hero.png" alt="Learn, certify, get hired" className="w-full h-32 object-cover" />
              <div className="bg-gradient-to-r from-teal-950 to-teal-800 px-4 py-2.5">
                <p className="text-white text-sm font-heading font-bold">Train. Certify. Get Hired.</p>
                <p className="text-teal-200 text-[11px]">Healthcare & Patient Care · Financial Literacy · Office Admin & Data · Skilled Trades</p>
                <div data-testid="login-mobile-flags" className="flex flex-wrap gap-1.5 mt-1.5">
                  {REGIONS.map((r) => (
                    <button key={r.code} type="button" title={r.name} onClick={() => setRegion(r.code)}
                      className={`rounded p-0.5 ${r.code === region ? "ring-1 ring-amber-300 bg-amber-400/25" : "bg-white/10"}`}>
                      <Flag code={r.code} className="w-7 h-[18px] rounded-[2px]" />
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">{mode === "login" ? "Welcome back 👋" : "Create your account"}</h2>
            <p className="text-sm text-slate-500 mt-1 mb-6">{mode === "login" ? "Sign in to continue your learning journey." : "Start learning in minutes — free for FSCD trainees."}</p>
            <form onSubmit={submit} className="space-y-4">
              {mode === "register" && (
                <input data-testid="register-name-input" className={input} placeholder={t("name")} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} required />
              )}
              <input data-testid="login-email-input" type="email" className={input} placeholder={t("email")} value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required />
              <input data-testid="login-password-input" type="password" className={input} placeholder={t("password")} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
              {error && <p data-testid="auth-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-3 py-2">{error}</p>}
              <button data-testid="login-submit-btn" disabled={busy}
                className="w-full h-12 rounded-xl bg-teal-800 text-white font-semibold hover:bg-teal-900 disabled:opacity-50 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-teal-800/20">
                {busy && <Loader2 size={16} className="animate-spin" />}{mode === "login" ? t("login") : t("register")}
              </button>
              <div className="flex justify-between text-sm">
                <button type="button" data-testid="toggle-auth-mode-btn" className="text-teal-700 font-semibold"
                  onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}>
                  {mode === "login" ? t("noAccount") : t("haveAccount")}
                </button>
                {mode === "login" && <Link data-testid="forgot-password-link" to="/forgot-password" className="text-slate-400 hover:text-slate-600">{t("forgotPassword")}</Link>}
              </div>
            </form>
            {isPreview && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-xs text-slate-500 space-y-1.5" data-testid="demo-credentials-box">
              <p className="font-semibold text-slate-700 flex items-center gap-1.5"><CheckCircle2 size={13} className="text-emerald-600" /> Demo accounts · password Demo123!</p>
              <div className="grid grid-cols-2 gap-x-3 gap-y-1 font-mono text-[11px]">
                <span>student@demo.org</span><span>instructor@demo.org</span>
                <span>admin@demo.org</span><span>superadmin@demo.org</span>
                <span>employer@demo.org</span>
              </div>
            </div>
            )}
            <p className="text-[10px] text-slate-400 mt-4">{t("internalNote")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
